import { Capacitor } from "@capacitor/core";

import { getAppUrl } from "./hostname";

const RECOVERY_PENDING_KEY = "mash_password_recovery_pending";
const LOGIN_PATH_PREFIXES = ["/login", "/forgot-password"];

function readRecoveryTypeFromUrl(): boolean {
  if (typeof window === "undefined") return false;

  const hash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  if (hash && new URLSearchParams(hash).get("type") === "recovery") {
    return true;
  }

  return new URLSearchParams(window.location.search).get("type") === "recovery";
}

/** True when the URL carries Supabase password-recovery tokens. */
export function isRecoveryUrl(): boolean {
  return readRecoveryTypeFromUrl();
}

export function markPasswordRecoveryPending(): void {
  sessionStorage?.setItem(RECOVERY_PENDING_KEY, "1");
}

/** Clears recovery flags and strips Supabase recovery tokens from the URL. */
export function clearPasswordRecoveryPending(): void {
  sessionStorage?.removeItem(RECOVERY_PENDING_KEY);

  if (typeof window === "undefined") return;
  if (!readRecoveryTypeFromUrl()) return;

  const { pathname, search } = window.location;
  window.history.replaceState(null, "", pathname + search);
}

function isPasswordRecoveryPending(): boolean {
  return sessionStorage?.getItem(RECOVERY_PENDING_KEY) === "1";
}

/** Recovery link clicked or PASSWORD_RECOVERY session — not a full Mash login. */
export function isRecoveryAuthState(recoverySessionActive: boolean): boolean {
  return (
    recoverySessionActive || isRecoveryUrl() || isPasswordRecoveryPending()
  );
}

export function isResetPasswordAppShell(
  pathname: string,
  recoverySessionActive: boolean,
): boolean {
  return (
    pathname === "/reset-password" && isRecoveryAuthState(recoverySessionActive)
  );
}

/** Active recovery session must finish on the reset form before using the app. */
export function shouldRedirectToPasswordReset(
  pathname: string,
  recoverySessionActive: boolean,
): boolean {
  if (pathname === "/reset-password") return false;

  if (
    LOGIN_PATH_PREFIXES.some(
      prefix => pathname === prefix || pathname.startsWith(`${prefix}/`),
    )
  ) {
    return false;
  }

  return isRecoveryAuthState(recoverySessionActive);
}

/**
 * Redirect target for resetPasswordForEmail. Uses the current browser origin in
 * web dev so Supabase accepts the URL (must match Site URL / redirect allow list).
 */
export function getPasswordResetRedirectUrl(): string {
  if (typeof window !== "undefined" && !Capacitor.isNativePlatform()) {
    return `${window.location.origin}/reset-password`;
  }
  return `${getAppUrl()}/reset-password`;
}

/** Call once when recovery is first detected from the URL. */
export function notePasswordRecoveryFromUrl(): boolean {
  if (!isRecoveryUrl()) return false;
  markPasswordRecoveryPending();
  return true;
}
